import { useRef, useState, useEffect } from "react";

/**
 * 3D floating tilt card. Uses perspective + mouse-tracked rotateX/rotateY
 * and a soft yellow glow that follows the cursor. Honors prefers-reduced-motion.
 */
export default function TiltCard({
  children,
  className = "",
  testId,
  maxTilt = 10, // degrees
  floatDelay = 0,
  as: Tag = "div",
}) {
  const ref = useRef(null);
  const [t, setT] = useState({ rx: 0, ry: 0, gx: 50, gy: 50 });
  const [reduced, setReduced] = useState(false);
  const rafRef = useRef(0);

  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(m.matches);
    on();
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, []);

  const onMove = (e) => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width; // 0..1
    const y = (e.clientY - r.top) / r.height;
    const rx = (0.5 - y) * maxTilt * 2;
    const ry = (x - 0.5) * maxTilt * 2;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() =>
      setT({ rx, ry, gx: x * 100, gy: y * 100 })
    );
  };
  const onLeave = () => {
    cancelAnimationFrame(rafRef.current);
    setT({ rx: 0, ry: 0, gx: 50, gy: 50 });
  };

  const transform = reduced
    ? "none"
    : `perspective(1100px) rotateX(${t.rx}deg) rotateY(${t.ry}deg) translateZ(0)`;

  return (
    <Tag
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={testId}
      className={`tilt-card group relative ${className}`}
      style={{
        transform,
        transformStyle: "preserve-3d",
        transition:
          "transform 400ms cubic-bezier(.2,.9,.2,1), box-shadow 400ms ease",
        animation: reduced ? "none" : `tiltFloat 7s ease-in-out ${floatDelay}s infinite alternate`,
        willChange: "transform",
      }}
    >
      {/* Cursor-tracked yellow glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{
          background: `radial-gradient(circle at ${t.gx}% ${t.gy}%, rgba(250,204,21,0.18) 0%, rgba(250,204,21,0) 45%)`,
        }}
      />
      {/* Inner content sits above glow */}
      <div className="relative" style={{ transform: reduced ? "none" : "translateZ(40px)" }}>
        {children}
      </div>
      {/* Under-card shadow that intensifies on hover */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-2 rounded-[inherit] -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: "radial-gradient(ellipse at 50% 90%, rgba(250,204,21,0.28) 0%, rgba(0,0,0,0) 60%)",
          filter: "blur(18px)",
        }}
      />
    </Tag>
  );
}
